import { useEffect, useState } from 'react';
import { GitCompare, Globe2, Info, Layers, Loader2 } from 'lucide-react';
import { apiClient } from '../../../api';
import { useAuth } from '../../../contexts/AuthContext';
import BasisManager from './BasisManager';
import type { ScopesResponse, ScopedSummary } from './types';

interface GapGroup {
  reason_text: string;
  count: number;
  apps: { eai_nbr: number; name: string; field_value: string | null }[];
}

interface Reconciliation {
  scope: string;
  basis_a: { key: string; title: string; predicate: string };
  basis_b: { key: string; title: string; predicate: string };
  extract: { as_of: string | null } | null;
  in_both: number;
  a_only: { count: number; groups: GapGroup[] };
  b_only: { count: number; groups: GapGroup[] };
}

export default function ScopeSummary({ refreshKey, scope, onScopeChange, basis, onBasisChange, onOpenImports, onOpenWorkbench, onConfigChanged }: {
  refreshKey: number;
  scope: string | null;
  onScopeChange: (scope: string) => void;
  basis: string;
  onBasisChange: (basis: string) => void;
  onOpenImports: () => void;
  onOpenWorkbench: () => void;
  onConfigChanged: () => void;
}) {
  const { user } = useAuth();
  const isAdmin = ['ADMIN', 'MASTER', 'SUPER_ADMIN'].includes(user?.role || '');
  const [scopes, setScopes] = useState<ScopesResponse | null>(null);
  const selected = scope;
  const [summary, setSummary] = useState<ScopedSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [recon, setRecon] = useState<Reconciliation | null>(null);
  const [reconOpen, setReconOpen] = useState(false);
  const [reconLoading, setReconLoading] = useState(false);
  const [reconB, setReconB] = useState('GOVERNED_UNIVERSE');

  useEffect(() => {
    if (!reconOpen || !selected) { setRecon(null); return; }
    let cancelled = false;
    setReconLoading(true);
    const basisB = reconB === basis ? (basis === 'GOVERNED_UNIVERSE' ? 'MIGRATION_SCOPE' : 'GOVERNED_UNIVERSE') : reconB;
    apiClient.get('/api/cloud-portfolio/reconciliation', { params: { scope: selected, basisA: basis, basisB } })
      .then((r) => { if (!cancelled) { setRecon(r.data); setError(null); } })
      .catch((e) => { if (!cancelled) setError(e?.response?.data?.error || e.message || 'Failed to reconcile bases'); })
      .finally(() => { if (!cancelled) setReconLoading(false); });
    return () => { cancelled = true; };
  }, [reconOpen, selected, basis, reconB, refreshKey]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    apiClient.get('/api/cloud-portfolio/scopes', { params: { basis } })
      .then((s) => {
        if (cancelled) return;
        setScopes(s.data);
        setError(null);
        const available = (s.data?.scopes || []).map((r: { scope: string }) => r.scope);
        if (available.length && (!scope || !available.includes(scope))) {
          onScopeChange(available[0]);
        }
      })
      .catch((e) => {
        if (cancelled) return;
        if (e?.response?.status === 404) setScopes(null);
        else setError(e?.response?.data?.error || e.message || 'Failed to load scopes');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [refreshKey, basis]);

  useEffect(() => {
    if (!selected) { setSummary(null); return; }
    let cancelled = false;
    setSummaryLoading(true);
    apiClient.get('/api/cloud-portfolio/summary', { params: { scope: selected, basis } })
      .then((r) => { if (!cancelled) { setSummary(r.data); setError(null); } })
      .catch((e) => { if (!cancelled) setError(e?.response?.data?.error || e.message || 'Failed to load summary'); })
      .finally(() => { if (!cancelled) setSummaryLoading(false); });
    return () => { cancelled = true; };
  }, [selected, basis, refreshKey]);

  const pickScope = (next: string) => {
    onScopeChange(next);
    apiClient.put('/api/cloud-portfolio/my-scope', { scope: next }).catch(() => {});
  };

  if (loading && !scopes) return <p className="text-xs text-gray-500">Loading scopes…</p>;

  if (!scopes) {
    return (
      <div className="flex items-start gap-2 text-xs text-gray-500 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2.5">
        <Info className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
        <span>
          No universe extract imported yet. Import the ServiceNow EAI extract from the{' '}
          <button onClick={onOpenImports} className="underline font-semibold text-amber-700">Data menu</button>
          {' '}to unlock datacentre scopes.
        </span>
      </div>
    );
  }

  const decidedTotal = summary ? summary.provenance.rule + summary.provenance.model + summary.provenance.human : 0;
  const bandPct = (n: number) => (decidedTotal ? Math.max(6, Math.round((n / decidedTotal) * 100)) : 0);

  return (
    <div className="space-y-5">
      {error && (
        <div className="flex items-start gap-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2.5">
          <Info className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
          {error}
        </div>
      )}
      <div className="bg-white border border-gray-200 rounded-xl p-4">
        <div className="flex items-center gap-2 mb-1">
          <Layers className="w-4 h-4 text-amber-500" />
          <span className="text-sm font-semibold text-super-dark-purple">Population basis — in scope according to whom</span>
          {loading && (
            <span className="flex items-center gap-1 text-[11px] text-amber-700 font-semibold">
              <Loader2 className="w-3 h-3 animate-spin" /> applying basis…
            </span>
          )}
        </div>
        <p className="text-xs text-gray-500 mb-2.5">
          Every count in this module carries both a datacentre scope and a basis. {scopes.default_basis} is the locked default; the others are lenses.
        </p>
        <div className="flex gap-1.5 flex-wrap mb-1.5">
          {scopes.bases.map((b) => (
            <button
              key={b.basis_key}
              onClick={() => onBasisChange(b.basis_key)}
              disabled={loading}
              title={b.description || b.predicate_text}
              className={`text-xs px-2.5 py-1 rounded-lg border transition-colors disabled:opacity-50 disabled:cursor-wait ${
                basis === b.basis_key
                  ? 'bg-amber-500 text-white border-amber-500'
                  : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
              }`}
            >
              {b.title}{b.basis_key === scopes.default_basis ? ' · default' : ''}
            </button>
          ))}
        </div>
        <p className={`text-[11px] text-gray-500 ${loading ? 'opacity-40' : ''}`}>
          {scopes.basis}: <span className="font-semibold text-gray-700">{scopes.basis_population}</span> of {scopes.universe_total} apps
          ({scopes.basis_predicate}) · extract as of {scopes.extract?.as_of || '—'}
        </p>
        {isAdmin && (
          <BasisManager scopes={scopes} basis={basis} onBasisChange={onBasisChange} onChanged={onConfigChanged} />
        )}
      </div>

      <div className={`bg-white border border-gray-200 rounded-xl p-4 ${loading ? 'opacity-40 pointer-events-none' : ''}`}>
        <div className="flex items-center gap-2 mb-1">
          <Globe2 className="w-4 h-4 text-amber-500" />
          <span className="text-sm font-semibold text-super-dark-purple">Scope — every count is scoped to a datacentre</span>
        </div>
        <p className="text-xs text-gray-500 mb-3">
          Counts below are under basis <span className="font-semibold text-gray-700">{scopes.basis}</span>. Your selection persists.
        </p>
        <div className="flex gap-1.5 flex-wrap">
          {scopes.scopes.map((s) => (
            <button
              key={s.scope}
              onClick={() => pickScope(s.scope)}
              className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${
                selected === s.scope
                  ? 'bg-super-dark-purple text-white border-super-dark-purple'
                  : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
              }`}
            >
              {s.scope} · {s.apps}
            </button>
          ))}
        </div>
      </div>

      {selected && (
        <div className="bg-white border border-gray-200 rounded-xl p-4">
          <button onClick={() => setReconOpen((o) => !o)} className="flex items-center gap-2 text-sm font-semibold text-super-dark-purple">
            <GitCompare className="w-4 h-4 text-amber-500" />
            Reconcile bases · {selected} — why don't your numbers match mine
            <span className="text-xs font-normal text-gray-400">{reconOpen ? 'hide' : 'show'}</span>
          </button>
          {reconOpen && (
            <div className="mt-3 space-y-3">
              <div className="flex items-center gap-2 text-xs text-gray-600 flex-wrap">
                <span className="font-semibold">{basis}</span>
                <span>vs</span>
                {scopes.bases.filter((b) => b.basis_key !== basis).map((b) => (
                  <button
                    key={b.basis_key}
                    onClick={() => setReconB(b.basis_key)}
                    className={`px-2 py-0.5 rounded border ${
                      (recon ? recon.basis_b.key : reconB) === b.basis_key
                        ? 'bg-super-dark-purple text-white border-super-dark-purple'
                        : 'bg-white border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    {b.basis_key}
                  </button>
                ))}
              </div>
              {reconLoading && <p className="text-xs text-gray-500 flex items-center gap-1.5"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Reconciling…</p>}
              {recon && !reconLoading && (
                <>
                  <div className="grid grid-cols-3 gap-3">
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                      <p className="text-[11px] text-gray-500">In both bases</p>
                      <p className="text-xl font-bold text-super-dark-purple">{recon.in_both}</p>
                    </div>
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                      <p className="text-[11px] text-gray-500">{recon.basis_a.key} only</p>
                      <p className="text-xl font-bold text-super-dark-purple">{recon.a_only.count}</p>
                    </div>
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                      <p className="text-[11px] text-gray-500">{recon.basis_b.key} only</p>
                      <p className="text-xl font-bold text-super-dark-purple">{recon.b_only.count}</p>
                    </div>
                  </div>
                  {[{ side: recon.b_only, title: `${recon.basis_b.key} only` }, { side: recon.a_only, title: `${recon.basis_a.key} only` }]
                    .filter(({ side }) => side.count > 0)
                    .map(({ side, title }) => (
                      <div key={title}>
                        <p className="text-xs font-semibold text-gray-700 mb-1">{title} — {side.count} apps, {side.groups.length} cause{side.groups.length === 1 ? '' : 's'}</p>
                        {side.groups.map((g) => (
                          <details key={g.reason_text} className="mb-1.5">
                            <summary className="text-xs text-gray-600 cursor-pointer">
                              <span className="font-semibold">{g.count} ×</span> {g.reason_text}
                            </summary>
                            <div className="pl-4 pt-1 space-y-0.5">
                              {g.apps.map((a) => (
                                <p key={a.eai_nbr} className="text-xs text-gray-600 flex items-center gap-2">
                                  EAI {a.eai_nbr} · {a.name}
                                  <button onClick={onOpenWorkbench} className="text-[11px] text-amber-700 underline">open workbench</button>
                                </p>
                              ))}
                            </div>
                          </details>
                        ))}
                      </div>
                    ))}
                  <p className="text-[11px] text-gray-400">
                    {recon.basis_a.key} = {recon.basis_a.predicate} · {recon.basis_b.key} = {recon.basis_b.predicate} · extract as of {recon.extract?.as_of || '—'}. The gap is a work queue, not a report.
                  </p>
                </>
              )}
            </div>
          )}
        </div>
      )}

      {summaryLoading && <p className="text-xs text-gray-500 flex items-center gap-1.5"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading {selected}…</p>}

      {summary && !summaryLoading && (
        <div className="bg-white border border-gray-200 rounded-xl p-4">
          <p className="text-xs font-semibold text-super-dark-purple mb-0.5">Dispositioning · {summary.scope}</p>
          <p className="text-[11px] text-gray-400 mb-3">Basis {summary.basis} ({summary.basis_predicate}) · extract as of {summary.extract?.as_of || '—'}</p>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-4">
            {[
              ['In scope', summary.in_scope],
              ['Decided', summary.decided],
              ['No decision yet', summary.no_decision],
              ['Migrates', summary.migrates],
              ['Does not migrate', summary.does_not_migrate],
            ].map(([label, value]) => (
              <div key={String(label)} className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                <p className="text-[11px] text-gray-500">{label}</p>
                <p className="text-xl font-bold text-super-dark-purple">{value}</p>
              </div>
            ))}
          </div>
          <p className="text-[11px] text-gray-500 mb-1.5">Decision provenance — how each call was made</p>
          {decidedTotal === 0 ? (
            <p className="text-xs text-gray-400">No decisions recorded for {summary.scope} under {summary.basis} yet.</p>
          ) : (
            <div className="space-y-1.5">
              <div className="flex h-6 rounded overflow-hidden max-w-md">
                {summary.provenance.rule > 0 && (
                  <div className="flex items-center justify-center text-[10px] font-semibold text-gray-700" style={{ width: `${bandPct(summary.provenance.rule)}%`, background: '#D3D1C7' }}>{summary.provenance.rule}</div>
                )}
                {summary.provenance.model > 0 && (
                  <div className="flex items-center justify-center text-[10px] font-semibold text-indigo-900" style={{ width: `${bandPct(summary.provenance.model)}%`, background: '#AFA9EC' }}>{summary.provenance.model}</div>
                )}
                {summary.provenance.human > 0 && (
                  <div className="flex items-center justify-center text-[10px] font-semibold text-emerald-950" style={{ width: `${bandPct(summary.provenance.human)}%`, background: '#5DCAA5' }}>{summary.provenance.human}</div>
                )}
              </div>
              <div className="flex gap-4 text-[11px] text-gray-600">
                <span><span className="inline-block w-2 h-2 rounded-sm mr-1" style={{ background: '#D3D1C7' }} />Rule · {summary.provenance.rule}</span>
                <span><span className="inline-block w-2 h-2 rounded-sm mr-1" style={{ background: '#AFA9EC' }} />Model · {summary.provenance.model}</span>
                <span><span className="inline-block w-2 h-2 rounded-sm mr-1" style={{ background: '#5DCAA5' }} />Human · {summary.provenance.human}</span>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
